<script setup>
import { useDocument } from '@/composables/useDocument'
import { useAbility } from '@casl/vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Document',
  },
})

const route = useRoute()
const router = useRouter()
const ability = useAbility()

const { document, versions, loading, saving, fetchDocument, fetchVersions, submitReview, publish, archive, createNewVersion } = useDocument()

const documentId = computed(() => route.params.id)
const tab = ref('detail')
const actionError = ref('')

const load = async () => {
  await fetchDocument(documentId.value)
  if (ability.can('view-history', 'Document')) {
    await fetchVersions(documentId.value)
  }
}

onMounted(load)

const statusColor = status => ({
  draft: 'secondary',
  review: 'warning',
  approved: 'info',
  published: 'success',
  archived: 'secondary',
}[status] ?? 'secondary')

const statusLabel = status => ({
  draft: 'Draft',
  review: 'Review',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
}[status] ?? status)

async function runAction(fn) {
  actionError.value = ''
  try {
    await fn(documentId.value)
    if (ability.can('view-history', 'Document')) await fetchVersions(documentId.value)
  } catch (e) {
    actionError.value = e?.data?.message ?? 'Aksi gagal diproses.'
  }
}

const currentVersion = computed(() => document.value?.latest_version)
</script>

<template>
  <div>
    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-3"
    />

    <template v-if="document">
      <VRow class="mb-4">
        <VCol cols="12">
          <div class="d-flex align-center justify-space-between flex-wrap gap-3">
            <div>
              <div class="d-flex align-center gap-2">
                <h4 class="text-h4 font-weight-bold mb-0">
                  {{ document.title }}
                </h4>
                <VChip
                  size="small"
                  :color="statusColor(document.status)"
                  variant="tonal"
                >
                  {{ statusLabel(document.status) }}
                </VChip>
              </div>
              <p class="text-body-2 text-medium-emphasis mb-0">
                <code>{{ document.document_number }}</code> · {{ document.category?.name }} · {{ document.unit?.name }}
              </p>
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <VBtn
                v-if="document.status === 'draft' && ability.can('update', 'Document')"
                variant="tonal"
                prepend-icon="ri-pencil-line"
                @click="router.push({ name: 'document-id-edit', params: { id: documentId } })"
              >
                Edit
              </VBtn>
              <VBtn
                v-if="document.status === 'draft' && ability.can('update', 'Document')"
                color="primary"
                :loading="saving"
                prepend-icon="ri-send-plane-line"
                @click="runAction(submitReview)"
              >
                Ajukan Review
              </VBtn>
              <VBtn
                v-if="document.status === 'approved' && ability.can('publish', 'Document')"
                color="success"
                :loading="saving"
                prepend-icon="ri-upload-cloud-2-line"
                @click="runAction(publish)"
              >
                Publish
              </VBtn>
              <VBtn
                v-if="document.status === 'published' && ability.can('update', 'Document')"
                variant="tonal"
                :loading="saving"
                prepend-icon="ri-file-add-line"
                @click="runAction(createNewVersion)"
              >
                Buat Versi Baru
              </VBtn>
              <VBtn
                v-if="document.status !== 'archived' && ability.can('archive', 'Document')"
                variant="tonal"
                color="secondary"
                :loading="saving"
                prepend-icon="ri-archive-line"
                @click="runAction(archive)"
              >
                Arsipkan
              </VBtn>
            </div>
          </div>
        </VCol>
      </VRow>

      <VAlert
        v-if="actionError"
        type="error"
        variant="tonal"
        density="compact"
        closable
        class="mb-4"
        @click:close="actionError = ''"
      >
        {{ actionError }}
      </VAlert>

      <VTabs
        v-model="tab"
        class="mb-4"
      >
        <VTab value="detail">
          Detail
        </VTab>
        <VTab
          v-if="ability.can('view-history', 'Document')"
          value="history"
        >
          Riwayat Revisi
        </VTab>
      </VTabs>

      <VWindow v-model="tab">
        <VWindowItem value="detail">
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VCard class="mb-4">
                <VCardTitle>File Dokumen ({{ currentVersion?.version_label ?? '-' }})</VCardTitle>
                <VCardText>
                  <EvidenceUploader
                    v-if="currentVersion"
                    evidenceable-type="document_version"
                    :evidenceable-id="currentVersion.id"
                  />
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VCard class="mb-4">
                <VCardTitle>Status Approval</VCardTitle>
                <VCardText>
                  <ApprovalStepper
                    approvable-type="document"
                    :approvable-id="documentId"
                  />
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </VWindowItem>

        <VWindowItem value="history">
          <VCard>
            <VCardText>
              <DocumentVersionHistory :versions="versions" />
            </VCardText>
          </VCard>
        </VWindowItem>
      </VWindow>
    </template>
  </div>
</template>
